/* The dialog both of the editor's pickers stand in, and the client list that is drawn inside one of
 * them. One stylesheet rather than two near-identical blocks, which is the duplication rule applied
 * at its second consumer — `ShelfQuestionDialog` reads it for the same reason.
 *
 * The EXERCISE picker's body is `entities/exercise-library`'s list since 17 September 2026 and
 * brings its own stylesheet; what it takes from here is the box, and `.popupWide` is that box at the
 * width its filter panel asks for. */

.backdrop {
  position: fixed;
  inset: 0;
  background: var(--scrim);
}

.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  gap: var(--space-4);
  width: var(--w-report-picker);
  max-width: calc(100vw - var(--space-12));
  max-height: calc(100dvh - var(--space-12));
  padding: var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  transform: translate(-50%, -50%);
}

/* The exercise picker. Its body is the plan builder's library — four filter tabs, a count row and
 * rows carrying a 48x36 plate — and 460px squeezes the `Sprzęt` tab's three-across tiles. */
.popupWide {
  width: var(--w-library-picker);
}

.head {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-dialog-title);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-modal);
  color: var(--text);
}

.lede {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.close {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: var(--sz-report-filter);
  height: var(--sz-report-filter);
  color: var(--text-2);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
}

.closeIcon {
  display: block;
  width: var(--icon-xs);
  height: var(--icon-xs);
}

/* ── „Kto dostaje tę ankietę" — the mobile app's assignment screen, transcribed ──────────────
 * Owner, 17 September 2026: the phone's own assign step is the drawing to follow. What that screen
 * does, read off `features/intake-survey/components/builder/AssignClientRow.tsx`: a row is a CARD
 * — 12 of padding, a 13 gap, a hairline edge, the large radius — carrying the person's disc, their
 * name over one line of meta, and an accent check at the end when they are chosen; a chosen row
 * takes the accent on its edge and doubles it with a ring. Everything here is that, in this app's
 * tokens.
 *
 * The one departure is the GROUND. On the phone the rows are `bgCard` on the screen's darker
 * canvas; here they stand INSIDE a `--bg-card` dialog, so a card-coloured row would be a hairline
 * drawn on its own colour. `--bg-inset` is the sunken step this app already uses for a field inside
 * a card, and it is what makes the row read as a row. */

.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  max-height: var(--h-report-picker-list);
  margin: var(--space-0);
  padding: var(--space-0);
  overflow-y: auto;
  list-style: none;
}

.row {
  display: flex;
  box-sizing: border-box;
  gap: var(--space-13px);
  align-items: center;
  width: 100%;
  padding: var(--space-3);
  font: inherit;
  color: var(--text);
  text-align: left;
  cursor: pointer;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-lg);
}

.row:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* Only a row that is NOT held brightens under the mouse. `.row:hover` and `.rowOn` tie on
 * specificity, so an unqualified hover won the border and a chosen row lost half its accent ring
 * the moment the pointer crossed it (owner, 17 September 2026). Held is held; the ring stays whole. */
.row:not(.rowOn):hover {
  border-color: var(--border-2);
}

/* Chosen. The accent is spent here on purpose and it is the owner's own call: this row IS a state
 * the trainer is setting, and the phone marks it exactly this way. `--ring-accent` doubles the
 * hairline instead of thickening the border, so the row's box does not change size. */
.rowOn {
  border-color: var(--accent);
  box-shadow: var(--ring-accent);
}

.rowText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: 0;
}

.rowLabel {
  overflow: hidden;
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The phone's `meta` line, at 12 / 500. `--text-2` and not the phone's own third ink: the 17
 * September round measured `--text-3` at 3.01:1 and 2.37:1 on this app's surfaces and took seven
 * pieces of read text off it, and an address is read. It is always rendered, so a client with no
 * e-mail keeps the same row height as one with — the list would otherwise be two heights deep. */
.rowHint {
  overflow: hidden;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The tick, in the accent, as the phone draws it. */
.rowMark {
  display: block;
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
  color: var(--accent-ink);
}

.empty {
  margin: var(--space-0);
  padding: var(--space-5) var(--space-0);
  font-size: var(--fs-note);
  color: var(--text-2);
  text-align: center;
}

.foot {
  display: flex;
  justify-content: flex-end;
}

/* „Gotowe" is the dialog's PRIMARY and wears the ember, like every other dialog's confirming
 * answer in this app. It was the ghost skin at a field's height — a grey rectangle beside nothing
 * else, which is what the owner called it. There is no „Anuluj" beside it and there must not be:
 * a row writes the moment it is pressed, so there is nothing left to cancel and a control offering
 * to would be a lie about what the dialog does. Geometry only; `emberButton` carries the rest. */
.done {
  height: var(--h-report-editor-field);
  padding: var(--space-0) var(--space-20px);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  letter-spacing: normal;
  border-radius: var(--r-button);
}
